import { FC, UIEvent } from 'react';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { documentCodeMatch, type PreparedDocumentCode } from './document-code.utils';

interface DocumentCodeMenuProps {
    options: PreparedDocumentCode[];
    query: string;
    onSelect: (code: string) => void;
    isLoading: boolean;
    width?: number;
    loadingLabel: string;
    emptyLabel: string;
    headerLabel?: string;
    onReachEnd?: () => void;
    hasMore?: boolean;
    moreLabel?: string;
}

const renderCode = (code: string, query: string, matchClass: string) => {
    const range = documentCodeMatch(code, query);
    if (!range) return code;

    return (
        <>
            {code.slice(0, range[0])}
            <span className={matchClass}>{code.slice(range[0], range[1])}</span>
            {code.slice(range[1])}
        </>
    );
};

const useStyles = createUseStyles({
    menu: {
        position: 'absolute',
        top: 0,
        left: 0,
        transform: 'translate3d(0px, 40px, 0px)',
        width: '100%',
        maxHeight: 300,
        overflow: 'auto',
        border: `1px solid ${colors.grayThree}`,
        boxShadow: '0 8px 12px 0 rgba(0, 0, 0, 0.16)',
        borderRadius: 3,
        backgroundColor: colors.white,
        zIndex: 10,
        padding: '4px 0',
    },
    option: {
        appearance: 'none',
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: 'transparent',
        padding: '8px 16px',
        fontSize: '1.7rem',
        lineHeight: '2.4rem',
        cursor: 'pointer',
        '&:hover, &:focus': {
            backgroundColor: colors.blueFour,
            outline: 'none',
        },
    },
    message: {
        padding: '8px 16px',
        fontSize: '1.5rem',
        color: colors.grayFive,
    },
    match: {
        backgroundColor: 'transparent',
        color: colors.blueOne,
        fontWeight: 700,
    },
    header: {
        padding: '8px 16px',
        fontSize: '1.3rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: colors.grayFive,
        borderBottom: `1px solid ${colors.grayThree}`,
        marginBottom: 4,
    },
});

export const DocumentCodeMenu: FC<DocumentCodeMenuProps> = ({
    options,
    query,
    onSelect,
    isLoading,
    width,
    loadingLabel,
    emptyLabel,
    headerLabel,
    onReachEnd,
    hasMore = false,
    moreLabel,
}) => {
    const classes = useStyles();

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        if (!onReachEnd || !hasMore) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollHeight - (scrollTop + clientHeight) < 48) {
            onReachEnd();
        }
    };

    return (
        <div className={classes.menu} style={width ? { width } : undefined} role="listbox" onScroll={handleScroll}>
            {headerLabel && !isLoading && options.length > 0 && <div className={classes.header}>{headerLabel}</div>}
            {isLoading && <div className={classes.message}>{loadingLabel}</div>}
            {!isLoading && options.length === 0 && <div className={classes.message}>{emptyLabel}</div>}
            {!isLoading &&
                options.map((option) => (
                    <button
                        key={option.code}
                        type="button"
                        role="option"
                        aria-selected={false}
                        aria-label={option.code}
                        className={classes.option}
                        onClick={() => onSelect(option.code)}
                    >
                        {renderCode(option.code, query, classes.match)}
                    </button>
                ))}
            {!isLoading && hasMore && moreLabel && <div className={classes.message}>{moreLabel}</div>}
        </div>
    );
};
